// Карточка компании — в том же живом виде, что карточка сделки: каркас components/card
// (CardShell, FieldSection/FieldRow, Timeline), слева вкладки, справа лента, которая
// остаётся на месте при смене вкладки. Полосы стадий нет — у компании нет воронки,
// и рисовать пустую полосу значило бы утверждать «стадии есть, но не заполнены».
//
// ДВА МИРА НА ОДНОЙ КАРТОЧКЕ, И ОНИ ПОМЕЧЕНЫ. Зеркальные поля Bitrix (название, тип,
// телефоны) показываются как есть, с капсулой «Bitrix», и не правятся: их источник
// правды — Bitrix, и кнопка правки здесь была бы обещанием чужой системы. Живая часть —
// СВОЯ схема ядра: лента (GET /api/cards/feed), комментарии (POST /api/cards-write/
// comment-add) и поля, которые владелец завёл в конструкторе («Наши поля»,
// GET /api/cards/fields + POST /api/cards-write/field-value-save). Стыковка — в
// api/cardLiveEntities.ts, одна на три карточки второй волны.
//
// КАКИМ ID ЖИВЁТ ЖИВАЯ ЧАСТЬ. Адрес карточки — номер Bitrix, а лента и поля ключуются
// номером СВОЕЙ схемы: он приезжает в `_OWN.id` ответа /own/company. Нет его — причина
// известна заранее (читаем зеркало / запись не перенесена), и карточка говорит её
// словами, не делая ни одного заведомо пустого запроса.
//
// Сделки, дебиторка и контакты — те же блоки, что были: отбор и расчёт долга не менялись
// (lib/payments.ts), иначе карточка и раздел «Дебиторка» показали бы разные суммы.
//
// РАЗДЕЛЫ ПОЛЕЙ — ПО ЖИВОМУ BITRIX. Пять разделов в том же порядке и с теми же полями,
// что на cp.cir.cx (раскладка снята из DOM 31.07.2026 — api/entityLayouts.ts). Сырые
// UF-поля своя база не отдаёт, поэтому карточка доспрашивает их у зеркала вторым запросом
// (useСырьёКарточкиBitrix) и честно пишет, когда зеркало не ответило.
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import {
  добавитьКомментарий, ownIdКарточки, почемуНедоступно, событияЛенты,
  сохранитьЗначение, словами, useЛентаКарточки, useНашиПоля, КЛЮЧ_НАШИХ_ПОЛЕЙ,
} from '../api/cardLiveEntities'
import {
  МАКЕТ_КОМПАНИИ, заметкаСырья, слитьДляРаскладки, useСырьёКарточкиBitrix,
} from '../api/entityLayouts'
import { useCompany, useContacts, useDeals, useFields, usePayments, useRefs, useRightsApi }
  from '../api/hooks'
import { ПОДСКАЗКА_НЕТ_СОЗДАНИЯ, подсказкаПустойДаты } from '../api/istochnik'
import { CardShell } from '../components/card/CardShell'
import type { CardTab } from '../components/card/CardShell'
import type { CardField } from '../components/card/FieldRow'
import { FieldSection } from '../components/card/FieldSection'
import { Timeline } from '../components/card/Timeline'
import type { CommentResult, TimelineEvent } from '../components/card/Timeline'
import { EntityLink } from '../components/EntityLink'
import { OwnFieldsSection } from '../components/OwnFieldsSection'
import { PaymentsTable } from '../components/PaymentsTable'
import { QueryState } from '../components/QueryState'
import { contactName } from '../lib/entities'
import { fmtDate, fmtMoney, fmtText, plural } from '../lib/format'
import { PAID_CATEGORY_ID, PAY, PAY_LIST_SELECT, paymentView, summarize } from '../lib/payments'

import { ПРОЧЕРК, РазделыBitrix } from '../components/cardEntities'

/** Сколько связанных записей показываем в карточке. Полный список — в своём разделе. */
const RELATED = 10

type Вкладка = 'main' | 'deals' | 'invoices' | 'contacts'

export default function CompanyCard() {
  const { id } = useParams<{ id: string }>()
  const q = useCompany(id)
  const refs = useRefs()
  const f = useFields('company')
  const rights = useRightsApi()
  const qc = useQueryClient()
  const [вкладка, setВкладка] = useState<Вкладка>('main')

  // Сделки и контакты компании. select ограничиваем: карточке не нужны все поля,
  // а ядро иначе тащит целый raw на каждую строку.
  const deals = useDeals({
    filter: id ? { COMPANY_ID: id } : {},
    order: { DATE_MODIFY: 'DESC' },
    select: ['ID', 'TITLE', 'STAGE_ID', 'CATEGORY_ID', 'OPPORTUNITY', 'CURRENCY_ID', 'DATE_MODIFY'],
  })
  const contacts = useContacts({
    filter: id ? { COMPANY_ID: id } : {},
    select: ['ID', 'NAME', 'LAST_NAME', 'SECOND_NAME'],
  })

  // Долг компании: неоплаченные платежи 134, ближайшие сроки первыми. Отбор тот же,
  // что на экране дебиторки (lib/payments.ts) — иначе карточка и список показали бы
  // разные суммы по одному клиенту.
  const payments = usePayments({
    filter: { companyId: id, [PAY.paidDate]: '', '!categoryId': PAID_CATEGORY_ID },
    order: { [PAY.dueDate]: 'ASC' },
    select: PAY_LIST_SELECT,
  }, !!id)
  const payRows = (payments.data?.items ?? []).map((p) => paymentView(p, refs))
  const payTotals = summarize(payRows)

  const c = q.data
  const ownId = ownIdКарточки(c)

  // Сырьё зеркала: полная запись Bitrix со всеми UF-полями. Своя база их не отдаёт,
  // а раскладка Bitrix почти целиком из них (под src=mirror хук в сеть не ходит).
  const сырьё = useСырьёКарточкиBitrix('crm.company.get', id)

  // Живая часть: лента и свои поля — только когда свой id известен (useЛентаКарточки
  // сама не ходит в сеть при ownId=null; причина отсутствия показывается словами).
  const лента = useЛентаКарточки('компания', ownId)
  const наши = useНашиПоля('компания', ownId)

  // События этой сессии (комментарий, правка поля): журнал ядро ведёт, но то, что человек
  // сделал только что, лента обязана показать без перечитки.
  const [сессионные, setСессионные] = useState<TimelineEvent[]>([])
  const добавитьСобытие = (с: Omit<TimelineEvent, 'id' | 'at'>) =>
    setСессионные((были) => [
      ...были,
      { ...с, id: `s-${были.length}`, at: new Date().toISOString() },
    ])

  const события = useMemo<TimelineEvent[]>(() => {
    const из: TimelineEvent[] = [...сессионные, ...событияЛенты(лента.data?.pages)]
    if (c?.DATE_CREATE) {
      из.push({
        id: 'created', at: String(c.DATE_CREATE), kind: 'bitrix',
        text: 'Компания заведена', origin: 'bitrix',
      })
    }
    return из
  }, [сессионные, лента.data, c])

  const заметкаЛенты = почемуНедоступно(ownId, лента.error)

  const отправитьКомментарий = async (текст: string): Promise<CommentResult> => {
    if (ownId === null) return { ok: false, почему: заметкаЛенты }
    try {
      const с = await добавитьКомментарий('компания', ownId, текст)
      добавитьСобытие({ kind: 'comment', text: String(с.text ?? текст), origin: 'crm' })
      return { ok: true }
    } catch (e) {
      return { ok: false, почему: словами(e) }
    }
  }

  const сохранитьНашеПоле = async (код: string, значение: unknown) => {
    await сохранитьЗначение('компания', ownId!, код, значение)
    const подпись = наши.data?.custom.find((п) => п.code === код)?.label ?? код
    добавитьСобытие({ kind: 'field', text: `Изменено поле «${подпись}»`, origin: 'crm' })
    await qc.invalidateQueries({ queryKey: [КЛЮЧ_НАШИХ_ПОЛЕЙ, 'компания', ownId] })
  }

  const вкладки: CardTab[] = [
    { key: 'main', label: 'Общие' },
    { key: 'deals', label: deals.data && !deals.data.stale ? `Сделки · ${deals.data.total}` : 'Сделки' },
    { key: 'invoices', label: 'Дебиторка' },
    {
      key: 'contacts',
      label: contacts.data && !contacts.data.stale ? `Контакты · ${contacts.data.total}` : 'Контакты',
    },
  ]

  return (
    <>
      <div className="page-head">
        <h1>{c?.TITLE ? fmtText(c.TITLE) : `Компания #${id}`}</h1>
        <div className="spacer" />
        <Link to="/companies">← к списку</Link>
      </div>

      <QueryState isLoading={q.isLoading} error={q.error}>
        {c && (
          <CardShell
            head={
              <div className="cshell-head">
                <div className="cshell-title">{fmtText(c.TITLE)}</div>
                <div className="spacer" />
                <span className="muted">Ответственный: {refs.userName(c.ASSIGNED_BY_ID)}</span>
              </div>
            }
            tabs={вкладки}
            active={вкладка}
            onTab={(k) => setВкладка(k as Вкладка)}
            left={
              <>
                {вкладка === 'main' && (
                  <Поля c={c} f={f} refs={refs} rights={rights} ownId={ownId}
                        наши={наши} сырьё={сырьё} contacts={contacts}
                        onSaveExt={rights.canWrite && ownId !== null ? сохранитьНашеПоле : undefined} />
                )}
                {вкладка === 'deals' && <Сделки id={id} deals={deals} refs={refs} />}
                {вкладка === 'invoices' && (
                  <Дебиторка id={id} payments={payments} payRows={payRows} payTotals={payTotals} />
                )}
                {вкладка === 'contacts' && <Контакты contacts={contacts} />}
              </>
            }
            right={
              <>
                <h2>Лента</h2>
                <Timeline
                  events={события}
                  onComment={rights.canWrite && ownId !== null ? отправитьКомментарий : undefined}
                  note={заметкаЛенты}
                  more={{
                    has: !!лента.hasNextPage,
                    loading: лента.isFetchingNextPage,
                    onMore: () => { void лента.fetchNextPage() },
                  }}
                  emptyText="Событий в ленте нет"
                />
              </>
            }
          />
        )}
      </QueryState>
    </>
  )
}

// ═══════════════════════ вкладка «Общие»: поля секциями ═══════════════════════

/**
 * Разделы — по живому Bitrix: «основаня информация о компании» (опечатка зеркальная),
 * «дополнительная информация», «контур. фокус», «оценка арбитражных дел…», «оквэд»
 * (МАКЕТ_КОМПАНИИ в api/entityLayouts.ts). Всё зеркальное: капсула Bitrix, без правки.
 * Плюс два своих раздела в конце: «Служебные» (даты) и «Наши поля» из конструктора.
 *
 * Три строки первого раздела в Bitrix — не поля, а виджеты, их собираем сами:
 * РЕКВИЗИТЫ — колонки своей схемы (`_OWN` ответа /own/company: зеркало реквизиты держит
 * в отдельном справочнике bx_requisites и в карточке не отдаёт), КОНТАКТЫ — привязанные
 * люди (та же выборка, что на вкладке «Контакты»), ID — номер записи.
 */
function Поля({ c, f, refs, rights, ownId, наши, сырьё, contacts, onSaveExt }: {
  c: NonNullable<ReturnType<typeof useCompany>['data']>
  f: ReturnType<typeof useFields>
  refs: ReturnType<typeof useRefs>
  rights: ReturnType<typeof useRightsApi>
  ownId: number | null
  наши: ReturnType<typeof useНашиПоля>
  сырьё: ReturnType<typeof useСырьёКарточкиBitrix>
  contacts: ReturnType<typeof useContacts>
  onSaveExt?: (code: string, value: unknown) => Promise<void>
}) {
  const own = (c._OWN ?? {}) as Record<string, unknown>
  const запись = слитьДляРаскладки(c, сырьё.data?.запись)

  // Реквизиты одной строкой, как виджет Bitrix: ИНН/КПП/ОГРН/полное название.
  const реквизиты = ([
    ['ИНН', own.inn], ['КПП', own.kpp], ['ОГРН', own.ogrn],
    ['Полное название по уставу', own.full_name],
  ] as Array<[string, unknown]>).filter(([, v]) => v)

  const люди = contacts.data?.items ?? []

  const особые: Record<string, CardField | null> = {
    REQUISITES: {
      code: 'REQUISITES', label: 'Реквизиты', type: 'text', origin: 'bitrix', readOnly: true,
      value: реквизиты.length ? 'есть' : null,
      display: реквизиты.length
        ? <>{реквизиты.map(([имя, v]) => <div key={имя}>{имя}: {fmtText(v)}</div>)}</>
        : ПРОЧЕРК,
      hint: реквизиты.length
        ? 'реквизиты — из своей схемы (перенос из Bitrix)'
        : ownId === null
          ? 'реквизиты Bitrix живут в отдельном справочнике (bx_requisites) и в ответ карточки не входят'
          : undefined,
    },
    CONTACT: {
      code: 'CONTACT', label: 'Контакты', type: 'text', origin: 'bitrix', readOnly: true,
      value: люди.length ? 'есть' : null,
      display: люди.length
        ? (
          <>
            {люди.slice(0, 3).map((п) => (
              <div key={п.ID}><EntityLink kind="contact" id={п.ID} title={contactName(п)} /></div>
            ))}
            {(contacts.data?.total ?? 0) > 3 && (
              <div className="hint">…и ещё {(contacts.data?.total ?? 0) - 3} на вкладке «Контакты»</div>
            )}
          </>
        )
        : ПРОЧЕРК,
    },
    ID: {
      code: 'ID', label: 'ID', type: 'text', origin: 'bitrix', readOnly: true,
      value: c.ID, display: <span className="mono">{fmtText(c.ID)}</span>,
    },
  }

  const служебные: CardField[] = [
    { code: 'DATE_CREATE', label: 'Создана', type: 'date', value: c.DATE_CREATE,
      origin: 'bitrix', readOnly: true, display: fmtDate(c.DATE_CREATE, true),
      hint: подсказкаПустойДаты(c.DATE_CREATE, ПОДСКАЗКА_НЕТ_СОЗДАНИЯ) },
    { code: 'DATE_MODIFY', label: 'Изменена', type: 'date', value: c.DATE_MODIFY,
      origin: 'bitrix', readOnly: true, display: fmtDate(c.DATE_MODIFY, true),
      hint: подсказкаПустойДаты(c.DATE_MODIFY) },
  ]

  return (
    <div className="card">
      {!f.loaded && (
        <div className="hint" style={{ marginBottom: 10 }}>
          Справочник полей недоступен — названия показаны по снимку раскладки 31.07.2026.
        </div>
      )}
      {f.loaded && f.stale && (
        <div className="hint" style={{ marginBottom: 10 }}>
          Справочник полей давно не обновлялся: если поле недавно переименовали,
          название может отставать.
        </div>
      )}
      <РазделыBitrix макет={МАКЕТ_КОМПАНИИ} запись={запись} f={f} refs={refs}
                     особые={особые} заметка={заметкаСырья(сырьё)} />
      <FieldSection title="Служебные" fields={служебные} />
      <OwnFieldsSection сущностьМн="компаний" ownId={ownId} наши={наши}
                        rights={rights} onSave={onSaveExt} />
    </div>
  )
}

// ═══════════════════════ вкладка «Сделки» ═══════════════════════

function Сделки({ id, deals, refs }: {
  id: string | undefined
  deals: ReturnType<typeof useDeals>
  refs: ReturnType<typeof useRefs>
}) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Сделки</h2>
        <div className="spacer" />
        <span className="muted">
          {deals.data && !deals.data.stale ? `всего: ${deals.data.total}` : ''}
        </span>
      </div>
      {/* Выборка сделок сужается правами роли. Без пометки «Сделок за этой
          компанией нет» читается как «клиент новый», а на деле его сделки ведёт
          не ваш отдел — и это разные поводы для звонка. */}
      <QueryState isLoading={deals.isLoading} error={deals.error}
                  stale={deals.data?.stale} ageMin={deals.data?.ageMin}
                  scope={deals.data?.scope}>
        {(deals.data?.items ?? []).length === 0 ? (
          <div className="empty"><div className="empty-title">Сделок за этой компанией нет</div></div>
        ) : (
          /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">ID</th>
                  <th>Название</th>
                  <th className="nowrap">Стадия</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {(deals.data?.items ?? []).slice(0, RELATED).map((d) => (
                  <tr key={d.ID}>
                    <td className="mono nowrap" data-label="ID">
                      <EntityLink kind="deal" id={d.ID} title={d.ID} />
                    </td>
                    <td data-label="Название">
                      <EntityLink kind="deal" id={d.ID} title={fmtText(d.TITLE)} />
                    </td>
                    <td className="nowrap" data-label="Стадия">
                      <span className={`badge ${refs.stageKind(d.STAGE_ID)}`}>
                        {refs.stageName(d.STAGE_ID)}
                      </span>
                    </td>
                    <td className="num" data-label="Сумма">{fmtMoney(d.OPPORTUNITY, d.CURRENCY_ID || 'RUB')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(deals.data?.total ?? 0) > RELATED && (
          <div style={{ marginTop: 10 }}>
            <Link to={`/deals?company=${id}`}>Показать все {deals.data?.total} →</Link>
          </div>
        )}
      </QueryState>
    </div>
  )
}

// ═══════════════════════ вкладка «Дебиторка» ═══════════════════════

function Дебиторка({ id, payments, payRows, payTotals }: {
  id: string | undefined
  payments: ReturnType<typeof usePayments>
  payRows: ReturnType<typeof paymentView>[]
  payTotals: ReturnType<typeof summarize>
}) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Дебиторка</h2>
        <div className="spacer" />
        {payments.data && !payments.data.stale && (
          <span className="muted">
            долг {fmtMoney(payTotals.total.sum)}
            {payTotals.buckets.overdue.count > 0 && (
              <span className="error">
                {' '}· просрочено {fmtMoney(payTotals.buckets.overdue.sum)}
              </span>
            )}
          </span>
        )}
      </div>
      {/* Дебиторка сужается правами роли, и здесь это дороже всего: под
          заголовком стоит «долг N ₽» — часть долга, выданная за весь. */}
      <QueryState isLoading={payments.isLoading} error={payments.error}
                  stale={payments.data?.stale} ageMin={payments.data?.ageMin}
                  scope={payments.data?.scope}>
        <PaymentsTable rows={payRows.slice(0, RELATED)} compact
                       stale={payments.data?.stale}
                       emptyText="Неоплаченных платежей за компанией нет" />
        {/* Долг считаем по загруженной странице (50 записей ядра): агрегатов
            ядро пока не отдаёт, поэтому у клиента с длинной историей сумма
            неполная — говорим об этом, а не показываем красивое неверное число. */}
        {payRows.length > 0 && (
          <div className="hint" style={{ marginTop: 8 }}>
            {payRows.length > RELATED && `В таблице первые ${RELATED} по сроку оплаты. `}
            {(payments.data?.total ?? 0) > payRows.length
              ? `Сумма долга — по ${plural(payRows.length, 'первой', 'первым', 'первым')}`
                + ` ${payRows.length}`
                + ` ${plural(payRows.length, 'записи', 'записям', 'записям')}`
                + ` из ${payments.data?.total}: агрегатов ядро пока не отдаёт.`
              : `Сумма долга — по ${plural(payRows.length, 'всему', 'всем', 'всем')}`
                + ` ${payRows.length}`
                + ` ${plural(payRows.length, 'неоплаченному платежу',
                  'неоплаченным платежам', 'неоплаченным платежам')} компании.`}
          </div>
        )}
        {(payments.data?.total ?? 0) > 0 && (
          <div style={{ marginTop: 10 }}>
            <Link to={`/receivables?company=${id}`}>
              Вся дебиторка компании ({payments.data?.total}) →
            </Link>
          </div>
        )}
      </QueryState>
    </div>
  )
}

// ═══════════════════════ вкладка «Контакты» ═══════════════════════

function Контакты({ contacts }: { contacts: ReturnType<typeof useContacts> }) {
  return (
    <div className="card">
      <h2>Контакты</h2>
      <QueryState isLoading={contacts.isLoading} error={contacts.error}
                  stale={contacts.data?.stale} ageMin={contacts.data?.ageMin}
                  scope={contacts.data?.scope}>
        {(contacts.data?.items ?? []).length === 0 ? (
          <div className="empty"><div className="empty-title">Контактов не привязано</div></div>
        ) : (
          <ul className="plain-list">
            {(contacts.data?.items ?? []).slice(0, RELATED).map((p) => (
              <li key={p.ID}>
                <EntityLink kind="contact" id={p.ID} title={contactName(p)} />
              </li>
            ))}
          </ul>
        )}
        {(contacts.data?.total ?? 0) > RELATED && (
          <div className="hint" style={{ marginTop: 8 }}>
            …и ещё {(contacts.data?.total ?? 0) - RELATED}
          </div>
        )}
      </QueryState>
    </div>
  )
}
